import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { Trash2, Store, Search, Loader2, AlertTriangle} from 'lucide-react';
import Pagination from './Pagination';
import { useToast } from './Toast';

const UsersManager = () => {
  const showToast = useToast();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);

  // --- ÉTATS POUR LE MODAL ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = useCallback(async (currentPage = page) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/super-admin/users?page=${currentPage}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      showToast("Impossible de charger les utilisateurs", "error");
    } finally {
      setLoading(false);
    }
  }, [page, showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openConfirmModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/super-admin/users/${userToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      showToast("Accès utilisateur révoqué.");
      fetchUsers();
    } catch (err) {
      showToast("Erreur lors de la révocation", "error");
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setLoading(true);
  };

  const filteredUsers = users.filter(u =>
    u.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.salonId?.nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative">
      
      {/* --- MODAL DE CONFIRMATION --- */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)} />
          <div className="bg-white rounded-t-[2rem] sm:rounded-[2rem] p-8 w-full max-w-md relative shadow-2xl animate-in slide-in-from-bottom sm:zoom-in duration-300">
            <div className="flex justify-center mb-4 sm:hidden">
              <div className="w-10 h-1 bg-slate-200 rounded-full" />
            </div>
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-6">
              <AlertTriangle size={32} />
            </div>
            <h3 className="text-xl font-black text-slate-800 mb-2">Révoquer l'accès ?</h3>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              Vous êtes sur le point de désactiver le compte de <span className="font-bold text-slate-700">{userToDelete?.prenom} {userToDelete?.nom}</span>. Cette action est irréversible.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="py-4 px-6 bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 transition-all"
              >
                Annuler
              </button>
              <button 
                onClick={confirmDelete}
                disabled={isDeleting}
                className="py-4 px-6 bg-red-500 text-white font-bold rounded-2xl hover:bg-red-600 shadow-lg shadow-red-200 transition-all flex items-center justify-center"
              >
                {isDeleting ? <Loader2 className="animate-spin" size={20} /> : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- RESTE DU COMPOSANT --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-black text-slate-800">Gestion Globale Utilisateurs</h2>
          <p className="text-slate-400 text-xs font-medium mt-1">
            {pagination ? `${pagination.total} comptes au total` : `${filteredUsers.length} comptes`}
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Rechercher..." 
            className="pl-12 pr-6 py-3 bg-slate-50 rounded-2xl border-none outline-none focus:ring-2 ring-blue-500/20 w-full md:w-80 transition-all"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={32} /></div>
      ) : (
        <>
          {/* DESKTOP : tableau */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50">
                  <th className="pb-4">Utilisateur</th>
                  <th className="pb-4">Salon</th>
                  <th className="pb-4">Rôle</th>
                  <th className="pb-4">Statut</th>
                  <th className="pb-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredUsers.map(user => (
                  <tr key={user._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-4">
                      <div className="font-bold text-slate-700">{user.prenom} {user.nom}</div>
                      <div className="text-xs text-slate-400 font-medium">{user.email}</div>
                      <div className="text-[10px] text-blue-500 font-bold">{user.telephone}</div>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center text-slate-600 font-medium italic text-[12px]">
                        <Store size={14} className="mr-2 text-blue-500" />
                        {user.salonId?.nom || "Non assigné"}
                      </div>
                    </td>
                    <td className="py-4 text-[10px] font-black uppercase">
                      {user.role === 1 ? 'Gérant' : 'Staff'}
                    </td>
                    <td className="py-4">
                      <span className={`text-[10px] font-bold flex items-center ${user.isActive !== false ? 'text-emerald-500' : 'text-red-400'}`}>
                        <span className="mr-1.5">●</span> {user.isActive !== false ? 'Actif' : 'Révoqué'}
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      <button
                        onClick={() => openConfirmModal(user)}
                        disabled={user.isActive === false}
                        className={`p-2 transition-all ${user.isActive !== false ? 'text-slate-300 hover:text-red-500 hover:scale-110' : 'text-slate-200 cursor-not-allowed'}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE : cartes */}
          <div className="md:hidden space-y-3">
            {filteredUsers.length === 0 ? (
              <p className="text-center py-12 text-slate-400 font-bold italic text-sm">Aucun utilisateur trouvé.</p>
            ) : (
              filteredUsers.map(user => (
                <div key={user._id} className="bg-slate-50 rounded-[1.5rem] p-4 border border-slate-100 space-y-3">
                  {/* Header carte */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-black text-slate-800 truncate">{user.prenom} {user.nom}</p>
                      <p className="text-xs text-slate-400 font-medium truncate">{user.email}</p>
                      {user.telephone && (
                        <p className="text-[10px] text-blue-500 font-bold mt-0.5">{user.telephone}</p>
                      )}
                    </div>
                    <button
                      onClick={() => openConfirmModal(user)}
                      disabled={user.isActive === false}
                      className={`p-2 rounded-xl transition-all shrink-0 ${user.isActive !== false ? 'text-slate-400 hover:text-red-500 hover:bg-red-50' : 'text-slate-200 cursor-not-allowed'}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {/* Infos secondaires */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center text-slate-500 font-medium italic text-xs gap-1.5">
                      <Store size={13} className="text-blue-500 shrink-0" />
                      <span className="truncate max-w-[120px]">{user.salonId?.nom || "Non assigné"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-slate-500 uppercase bg-slate-200 px-2 py-0.5 rounded-full">
                        {user.role === 1 ? 'Gérant' : 'Staff'}
                      </span>
                      <span className={`text-[10px] font-black flex items-center gap-1 ${user.isActive !== false ? 'text-emerald-600' : 'text-red-500'}`}>
                        <span>●</span>{user.isActive !== false ? 'Actif' : 'Révoqué'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <Pagination pagination={pagination} onPageChange={handlePageChange} />
    </div>
  );
};

export default UsersManager;